import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CogIcon,
  ChartBarIcon,
  CalendarIcon,
  UserIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon as ClockOutlineIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useGetLoanRequestsQuery,
  useApproveLoanRequestMutation,
  useRejectLoanRequestMutation,
  useGetLoanRequestQuery
} from '../../services/loanApi';
import { Card, Button, Input, Select, Modal, DataTable, LoadingSpinner } from '../../components/common';
import { setFilters, setCurrentPage } from '../../store/slices/loanSlice';
import socketService from '../../services/socketService';
import toast from 'react-hot-toast';

const AdminLoanManagement = () => {
  const { t } = useTranslation(['common', 'loans']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { filters, pagination } = useSelector((state) => state.loan);

  // Modal states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [remarks, setRemarks] = useState('');

  // API hooks
  const { data: loanRequestsData, isLoading, refetch } = useGetLoanRequestsQuery({
    page: pagination.currentPage,
    limit: pagination.limit,
    ...filters
  });

  const [approveLoan, { isLoading: approving }] = useApproveLoanRequestMutation();
  const [rejectLoan, { isLoading: rejecting }] = useRejectLoanRequestMutation();

  const { data: loanDetails } = useGetLoanRequestQuery(
    selectedLoan?._id || selectedLoan?.id,
    { skip: !selectedLoan || !showDetailsModal }
  );

  // Filter options
  const statusOptions = [
    { value: '', label: t('common:allStatuses') },
    { value: 'submitted', label: t('loans:submitted') },
    { value: 'pending', label: t('loans:pending') },
    { value: 'approved', label: t('loans:approved') },
    { value: 'rejected', label: t('loans:rejected') },
    { value: 'cancelled', label: t('loans:cancelled') },
    { value: 'active', label: t('loans:active') },
    { value: 'completed', label: t('loans:completed') }
  ];

  const loanTypeOptions = [
    { value: '', label: t('common:allTypes') },
    { value: 'salary_advance', label: t('loans:salaryAdvance') },
    { value: 'personal_loan', label: t('loans:personalLoan') },
    { value: 'emergency_loan', label: t('loans:emergencyLoan') }
  ];

  const handleFilterChange = (name, value) => {
    dispatch(setFilters({ [name]: value }));
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleApprove = async () => {
    if (!remarks.trim()) {
      toast.error(t('loans:approvalRemarksRequired'));
      return;
    }

    try {
      await approveLoan({
        id: selectedLoan._id || selectedLoan.id,
        remarks: remarks.trim()
      }).unwrap();

      toast.success(t('loans:loanApprovedSuccessfully'));
      setShowApprovalModal(false);
      setSelectedLoan(null);
      setRemarks('');
      refetch();
    } catch (error) {
      console.error('Error approving loan:', error);
      toast.error(error.data?.message || t('loans:approvalFailed'));
    }
  };

  const handleReject = async () => {
    if (!remarks.trim()) {
      toast.error(t('loans:rejectionRemarksRequired'));
      return;
    }

    try {
      await rejectLoan({
        id: selectedLoan._id || selectedLoan.id,
        remarks: remarks.trim()
      }).unwrap();

      toast.success(t('loans:loanRejectedSuccessfully'));
      setShowRejectionModal(false);
      setSelectedLoan(null);
      setRemarks('');
      refetch();
    } catch (error) {
      console.error('Error rejecting loan:', error);
      toast.error(error.data?.message || t('loans:rejectionFailed'));
    }
  };

  const openApprovalModal = (loan) => {
    setSelectedLoan(loan);
    setRemarks('');
    setShowApprovalModal(true);
  };

  const openRejectionModal = (loan) => {
    setSelectedLoan(loan);
    setRemarks('');
    setShowRejectionModal(true);
  };

  const openDetailsModal = (loan) => {
    setSelectedLoan(loan);
    setShowDetailsModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleSolidIcon className="h-5 w-5 text-red-500" />;
      case 'active':
        return <ClockSolidIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckBadgeIcon className="h-5 w-5 text-purple-500" />;
      case 'cancelled':
        return <XCircleSolidIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const columns = [
    {
      key: 'applicationNumber',
      header: 'Application #',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md flex items-center justify-center">
              <DocumentTextIcon className="h-3 w-3 text-white" />
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900 text-xs">
              {row.applicationNumber}
            </div>
            <div className="text-xs text-gray-500">
              {row._id?.slice(-4).toUpperCase()}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-white">
                {row.employeeId?.firstName?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 text-xs truncate">
              {row.employeeId?.firstName} {row.employeeId?.lastName}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {row.employeeId?.employeeId}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'loanType',
      header: 'Type',
      render: (row) => {
        const getLoanTypeIcon = (type) => {
          switch (type) {
            case 'salary_advance':
              return <BanknotesIcon className="h-3 w-3 text-green-600" />;
            case 'personal_loan':
              return <UserIcon className="h-3 w-3 text-blue-600" />;
            case 'emergency_loan':
              return <ExclamationTriangleIcon className="h-3 w-3 text-red-600" />;
            default:
              return <DocumentTextIcon className="h-3 w-3 text-gray-600" />;
          }
        };

        const getLoanTypeColor = (type) => {
          switch (type) {
            case 'salary_advance':
              return 'bg-green-100 text-green-800 border-green-200';
            case 'personal_loan':
              return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'emergency_loan':
              return 'bg-red-100 text-red-800 border-red-200';
            default:
              return 'bg-gray-100 text-gray-800 border-gray-200';
          }
        };

        return (
          <div className="flex items-center space-x-1">
            {getLoanTypeIcon(row.loanType)}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getLoanTypeColor(row.loanType)}`}>
              {row.loanType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (row) => (
        <div className="text-right">
          <div className="font-semibold text-gray-900 text-sm">
            ₹{row.amount?.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-gray-500">
            {row.repaymentType === 'emi_installments' ? 'EMI' : 'Full'}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex items-center justify-center">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)} shadow-sm`}>
            {getStatusIcon(row.status)}
            <span className="ml-1 capitalize">{row.status}</span>
          </span>
        </div>
      )
    },
    {
      key: 'appliedAt',
      header: 'Applied',
      render: (row) => (
        <div className="text-center">
          <div className="font-medium text-gray-900 text-xs">
            {new Date(row.appliedAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short'
            })}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(row.appliedAt).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center justify-center space-x-0.5">
          <button
            onClick={() => openDetailsModal(row)}
            className="inline-flex items-center px-1.5 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            title="View Details"
          >
            <EyeIcon className="h-3 w-3" />
          </button>

          {(row.status === 'submitted' || row.status === 'pending') && (
            <>
              <button
                onClick={() => openApprovalModal(row)}
                className="inline-flex items-center px-1.5 py-1 border border-green-300 rounded text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 hover:border-green-400 transition-all duration-200 shadow-sm"
                title="Approve"
              >
                <CheckCircleIcon className="h-3 w-3" />
              </button>

              <button
                onClick={() => openRejectionModal(row)}
                className="inline-flex items-center px-1.5 py-1 border border-red-300 rounded text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-all duration-200 shadow-sm"
                title="Reject"
              >
                <XCircleIcon className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('loans:adminLoanManagement')} - HRMS</title>
      </Helmet>

      {/* Modern Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
              >
                <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="hidden xs:inline">{t('common:back')}</span>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <BanknotesIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  {t('loans:adminLoanManagement')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  {t('loans:manageLoanRequests')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">

          {/* Compact Stats Cards */}
          {loanRequestsData?.stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-yellow-100 rounded-md">
                      <ClockIcon className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600">
                        {t('loans:pendingRequests')}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {loanRequestsData.stats.pendingRequests}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600">Approved</p>
                      <p className="text-lg font-bold text-gray-900">
                        {loanRequestsData.stats.approvedRequests}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-red-100 rounded-md">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600">Cancelled</p>
                      <p className="text-lg font-bold text-gray-900">
                        {loanRequestsData.stats.cancelledRequests || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <BanknotesIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600">
                        {t('loans:activeLoans')}
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {loanRequestsData.stats.activeLoans}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <ChartBarIcon className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600">Total Amount</p>
                      <p className="text-sm font-bold text-gray-900">
                        ₹{(loanRequestsData.stats.totalLoanAmount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Table Card */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">

            <div className="relative z-10">
              {/* Filters */}
              <div className="bg-gradient-to-r from-blue-50/30 to-indigo-50/30 rounded-lg p-4 mb-4 border border-blue-100/40">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <h4 className="text-base font-semibold text-gray-900 flex items-center">
                    <FunnelIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Filters & Search
                  </h4>
                  <Button
                    onClick={() => dispatch(setFilters({
                      status: '',
                      loanType: '',
                      search: ''
                    }))}
                    size="sm"
                    variant="secondary"
                    className="px-3 py-1.5 bg-white/80 hover:bg-white border border-gray-300 text-gray-700 rounded-md transition-all duration-200 shadow-sm text-xs"
                  >
                    <XMarkIcon className="h-3.5 w-3.5 mr-1.5" />
                    Clear All
                  </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search applications..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/90 transition-all duration-200 text-sm"
                    />
                  </div>
                </div>

                {/* Filter Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <ClockIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                      Status
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {statusOptions.slice(0, 4).map((status) => (
                        <button
                          key={status.value}
                          onClick={() => handleFilterChange('status', status.value)}
                          className={`px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all duration-200 ${
                            filters.status === status.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Loan Type Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                      <BanknotesIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                      Loan Type
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {loanTypeOptions.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => handleFilterChange('loanType', type.value)}
                          className={`px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all duration-200 ${
                            filters.loanType === type.value
                              ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Table */}
              <div className="mt-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/60 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200/60 bg-gradient-to-r from-gray-50/50 to-blue-50/30">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-2 text-blue-600" />
                      Loan Applications ({loanRequestsData?.pagination?.total || 0})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <DataTable
                      columns={columns}
                      data={loanRequestsData?.data || []}
                      loading={isLoading}
                      pagination={loanRequestsData?.pagination}
                      onPageChange={handlePageChange}
                      emptyMessage={t('loans:noLoanRequests')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title={t('loans:approveLoanRequest')}
        size="md"
      >
        <div className="space-y-6">
          {/* Loan Summary Card */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900">
                  {selectedLoan?.applicationNumber}
                </h3>
                <p className="text-sm text-green-700">
                  {selectedLoan?.employeeId?.firstName} {selectedLoan?.employeeId?.lastName} • ₹{selectedLoan?.amount?.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {selectedLoan?.loanType?.replace('_', ' ') || 'N/A'} • Applied on {new Date(selectedLoan?.appliedAt).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>
          </div>

          {/* Approval Details */}
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Approval Confirmation</h4>
              <p className="text-sm text-blue-700">
                You are about to approve this loan request. This action will change the loan status to "approved" and notify the employee.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2 text-blue-600" />
                {t('loans:approvalRemarks')} <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 resize-none"
                placeholder={t('loans:enterApprovalRemarks')}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setShowApprovalModal(false)}
              className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-200 font-medium"
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleApprove}
              loading={approving}
              disabled={!remarks.trim()}
              className="px-8 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {t('common:approve')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        title={t('loans:rejectLoanRequest')}
        size="md"
      >
        <div className="space-y-6">
          {/* Loan Summary Card */}
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900">
                  {selectedLoan?.applicationNumber}
                </h3>
                <p className="text-sm text-red-700">
                  {selectedLoan?.employeeId?.firstName} {selectedLoan?.employeeId?.lastName} • ₹{selectedLoan?.amount?.toLocaleString()}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {selectedLoan?.loanType?.replace('_', ' ') || 'N/A'} • Applied on {new Date(selectedLoan?.appliedAt).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>
          </div>

          {/* Rejection Details */}
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Rejection Confirmation
              </h4>
              <p className="text-sm text-red-700">
                You are about to reject this loan request. This action cannot be undone and will notify the employee of the rejection.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2 text-red-600" />
                {t('loans:rejectionRemarks')} <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 resize-none"
                placeholder={t('loans:enterRejectionRemarks')}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setShowRejectionModal(false)}
              className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-200 font-medium"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              loading={rejecting}
              disabled={!remarks.trim()}
              className="px-8 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="h-5 w-5 mr-2" />
              {t('common:reject')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t('loans:loanDetails')}
        size="lg"
      >
        {loanDetails?.data && Object.keys(loanDetails.data).length > 0 ? (
          <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900">
                      {loanDetails.data.applicationNumber}
                    </h3>
                    <p className="text-sm text-blue-700">
                      {loanDetails.data.employeeId?.firstName} {loanDetails.data.employeeId?.lastName} • {loanDetails.data.employeeId?.employeeId}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(loanDetails.data.status)}`}>
                    {getStatusIcon(loanDetails.data.status)}
                    <span className="ml-2 capitalize">{loanDetails.data.status}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Loan Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BanknotesIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Loan Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Loan Type</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {loanDetails.data.loanType?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Amount</span>
                    <span className="text-lg font-bold text-green-600">
                      ₹{loanDetails.data.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Applied Date</span>
                    <span className="text-sm text-gray-900">
                      {new Date(loanDetails.data.appliedAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Repayment Type</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {loanDetails.data.repaymentType?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Employee Information */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Employee Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Name</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {loanDetails.data.employeeId?.firstName} {loanDetails.data.employeeId?.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Employee ID</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.employeeId}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Department</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.department?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Designation</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.designation?.name || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Purpose */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-blue-600" />
                Purpose
              </h4>
              <p className="text-gray-700 bg-gray-50 rounded-lg p-4">
                {loanDetails.data.purpose}
              </p>
            </div>

            {/* EMI Details */}
            {loanDetails.data.emiDetails && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
                  EMI Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {loanDetails.data.emiDetails.numberOfInstallments}
                    </p>
                    <p className="text-sm text-gray-600">Installments</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      ₹{loanDetails.data.amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">Loan Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      ₹{loanDetails.data.totalPaid?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-600">Total Paid</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">
                      ₹{loanDetails.data.remainingBalance?.toLocaleString() || loanDetails.data.amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">Remaining Balance</p>
                  </div>
                </div>
              </div>
            )}

            {/* Repayment Schedule */}
            {loanDetails.data.repaymentSchedule && loanDetails.data.repaymentSchedule.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Repayment Schedule
                </h4>
                <div className="space-y-3">
                  {loanDetails.data.repaymentSchedule.map((installment, index) => {
                    const getInstallmentStatusColor = (status) => {
                      switch (status) {
                        case 'paid':
                          return 'bg-green-100 text-green-800 border-green-200';
                        case 'pending':
                          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
                        case 'overdue':
                          return 'bg-red-100 text-red-800 border-red-200';
                        default:
                          return 'bg-gray-100 text-gray-800 border-gray-200';
                      }
                    };

                    const getInstallmentStatusIcon = (status) => {
                      switch (status) {
                        case 'paid':
                          return <CheckCircleSolidIcon className="h-4 w-4" />;
                        case 'pending':
                          return <ClockSolidIcon className="h-4 w-4" />;
                        case 'overdue':
                          return <XCircleSolidIcon className="h-4 w-4" />;
                        default:
                          return <ClockIcon className="h-4 w-4" />;
                      }
                    };

                    return (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getInstallmentStatusColor(installment.status)}`}>
                              {getInstallmentStatusIcon(installment.status)}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Installment {installment.installmentNumber}
                            </p>
                            <p className="text-xs text-gray-600">
                              Due: {new Date(installment.dueDate).toLocaleDateString('en-GB')}
                              {installment.paidAt && (
                                <span className="ml-2">
                                  • Paid: {new Date(installment.paidAt).toLocaleDateString('en-GB')}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            ₹{installment.amount?.toLocaleString()}
                          </p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getInstallmentStatusColor(installment.status)}`}>
                            {getInstallmentStatusIcon(installment.status)}
                            <span className="ml-1 capitalize">{installment.status}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Decision Information */}
            {loanDetails.data.finalDecision && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckBadgeIcon className="h-5 w-5 mr-2 text-green-600" />
                  Decision Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Decided By</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {loanDetails.data.finalDecision.decidedBy?.firstName && loanDetails.data.finalDecision.decidedBy?.lastName
                        ? `${loanDetails.data.finalDecision.decidedBy.firstName} ${loanDetails.data.finalDecision.decidedBy.lastName}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Decision Date</span>
                    <span className="text-sm text-gray-900">
                      {new Date(loanDetails.data.finalDecision.decidedAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 block mb-2">Remarks</span>
                    <p className="text-sm text-gray-900 bg-white rounded-md p-3 border">
                      {loanDetails.data.finalDecision.remarks}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <LoadingSpinner />
              <p className="text-sm text-gray-600 mt-4">Loading loan details...</p>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AdminLoanManagement;